import axios from 'axios';

const api = axios;

const inFlightRequests = new Map<string, Promise<any>>();

const dedupeRequest = <T>(key: string, requestFn: () => Promise<T>): Promise<T> => {
  const existing = inFlightRequests.get(key);
  if (existing) {
    return existing as Promise<T>;
  }
  
  const promise = requestFn().finally(() => {
    inFlightRequests.delete(key);
  });
  
  inFlightRequests.set(key, promise);
  return promise;
};

export type TimePeriod = 'week' | 'month' | 'quarter' | 'year' | 'custom';

export interface AnalyticsParams {
  period?: TimePeriod;
  startDate?: string;
  endDate?: string;
}

export interface PreviousPeriod {
  start_date: string;
  end_date: string;
  total_spent: number;
  total_income: number;
}

export interface AnalyticsSummary {
  period: string;
  start_date: string;
  end_date: string;
  total_spent: number;
  total_income: number;
  net: number;
  transaction_count: number;
  avg_transaction: number;
  previous_period?: PreviousPeriod;
  spending_change_percent?: string;
  income_change_percent?: string;
}

export interface CategoryBreakdown {
  category: string;
  total_spent: number;
  percentage: number;
  transaction_count: number;
}

export interface CategoryAnalytics {
  period: string;
  start_date: string;
  end_date: string;
  total_spending: number;
  categories: CategoryBreakdown[];
}

export interface TrendDataPoint {
  date: string;
  spent: number;
  income: number;
  net: number;
  transaction_count: number;
}

export interface TrendsAnalytics {
  granularity: string;
  months: number;
  start_date: string;
  end_date: string;
  average_spent: number;
  trends: TrendDataPoint[];
}

export interface MerchantSpending {
  merchant: string;
  total_spent: number;
  transaction_count: number;
  avg_transaction: number;
  logo_url: string | null;
}

export interface TopMerchantsAnalytics {
  period: string;
  start_date: string;
  end_date: string;
  merchants: MerchantSpending[];
}

export interface DailyDataPoint {
  date: string;
  spending: number;
  income: number;
  net: number;
  transaction_count: number;
}

export interface DailyAnalytics {
  period: string;
  start_date: string;
  end_date: string;
  data_points: DailyDataPoint[];
}

export interface ClearCacheResponse {
  message: string;
  clearedAt: string;
}

export interface CreditAccount {
  account_id: string;
  name: string;
  balance: number;
  credit_limit: number;
  available_credit: number;
  utilization_percent: string;
}

export interface CreditBalances {
  total_balance: number;
  total_credit_limit: number;
  total_available_credit: number;
  overall_utilization_percent: string;
  accounts: CreditAccount[];
}

export interface SavingsSubtype {
  balance: number;
  account_count: number;
  accounts: Array<{
    account_id: string;
    name: string;
    balance: number;
    subtype: string;
  }>;
}

export interface SavingsAnalytics {
  total_balance: number;
  savings_total: number;
  checking_total: number;
  by_subtype: Record<string, SavingsSubtype>;
}

export interface AccountTypeBreakdown {
  total: number;
  account_count: number;
  accounts: Array<{
    account_id: string;
    name: string;
    balance: number;
    type: string;
    subtype: string;
  }>;
}

export interface AccountOverview {
  net_worth: number;
  total_assets: number;
  total_liabilities: number;
  by_type: Record<string, AccountTypeBreakdown>;
}

export interface CreditReturnRefund {
  transaction_id: string;
  date: string;
  amount: number;
  name: string;
  merchant_name: string | null;
  logo_url: string | null;
  account_name: string;
  account_mask: string;
}

export interface CreditReturnOriginalPurchase {
  transaction_id: string;
  date: string;
  amount: number;
  name: string;
  merchant_name: string | null;
  logo_url: string | null;
  days_between: number;
}

export interface CreditReturn {
  refund: CreditReturnRefund;
  original_purchase: CreditReturnOriginalPurchase | null;
  matched: boolean;
}

export interface CreditReturnsAnalytics {
  negative_days: number;
  positive_days: number;
  count: number;
  matched_count: number;
  unmatched_count: number;
  total_returned: number;
  returns: CreditReturn[];
  cached: boolean;
}

const buildQueryString = (params: AnalyticsParams): string => {
  const queryParams = new URLSearchParams();
  if (params.period) queryParams.append('period', params.period);
  if (params.startDate) queryParams.append('startDate', params.startDate);
  if (params.endDate) queryParams.append('endDate', params.endDate);
  const query = queryParams.toString();
  return query ? `?${query}` : '';
};

export const analyticsService = {
  getSummary: (params: AnalyticsParams = {}): Promise<AnalyticsSummary> => {
    const key = `summary${buildQueryString(params)}`;
    return dedupeRequest(key, async () => {
      const response = await api.get(`/analytics/summary${buildQueryString(params)}`);
      return response.data;
    });
  },

  getByCategory: (params: AnalyticsParams = {}): Promise<CategoryAnalytics> => {
    const key = `by-category${buildQueryString(params)}`;
    return dedupeRequest(key, async () => {
      const response = await api.get(`/analytics/by-category${buildQueryString(params)}`);
      return response.data;
    });
  },

  getTrends: (params: AnalyticsParams = {}): Promise<TrendsAnalytics> => {
    const key = `trends${buildQueryString(params)}`;
    return dedupeRequest(key, async () => {
      const response = await api.get(`/analytics/trends${buildQueryString(params)}`);
      return response.data;
    });
  },

  getTopMerchants: (params: AnalyticsParams = {}): Promise<TopMerchantsAnalytics> => {
    const key = `top-merchants${buildQueryString(params)}`;
    return dedupeRequest(key, async () => {
      const response = await api.get(`/analytics/top-merchants${buildQueryString(params)}`);
      return response.data;
    });
  },

  getDaily: (params: AnalyticsParams = {}): Promise<DailyAnalytics> => {
    const key = `daily${buildQueryString(params)}`;
    return dedupeRequest(key, async () => {
      const response = await api.get(`/analytics/daily${buildQueryString(params)}`);
      return response.data;
    });
  },

  clearCache: async (): Promise<ClearCacheResponse> => {
    const response = await api.post('/analytics/clear-cache');
    return response.data;
  },

  getCreditBalances: (): Promise<CreditBalances> => {
    const key = 'credit-balances';
    return dedupeRequest(key, async () => {
      const response = await api.get('/analytics/credit-balances');
      return response.data;
    });
  },

  getSavings: (): Promise<SavingsAnalytics> => {
    const key = 'savings';
    return dedupeRequest(key, async () => {
      const response = await api.get('/analytics/savings');
      return response.data;
    });
  },

  getAccountOverview: (): Promise<AccountOverview> => {
    const key = 'accounts';
    return dedupeRequest(key, async () => {
      const response = await api.get('/analytics/accounts');
      return response.data;
    });
  },

  getCreditReturns: (): Promise<CreditReturnsAnalytics> => {
    const key = 'credit-returns';
    return dedupeRequest(key, async () => {
      const response = await api.get('/analytics/credit-returns');
      return response.data;
    });
  },
};

export default analyticsService;
